import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import {
  DialogTypes,
  GeneralIds,
  ModelTypes,
  RoutePaths,
} from '../../types/types';
import { useSetRecoilState } from 'recoil';
import {
  confirmDialogState,
  dialogState,
  ToastSeverity,
  toastStateSelector,
} from '../../state';
import { useNavigate, useParams } from 'react-router-dom';
import { getRoutePath } from '../../utils';
import { FormTemplateType } from 'api/models';
import { remove } from 'operations';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import { getLatestTemplate } from 'routes/ProjectFormLists/FormTemplateTypesTable';

interface ReportTemplatesTableProps {
  templates: FormTemplateType[];
  loading: boolean;
  refetch?: () => void;
}

export default function ReportTemplatesTable({
  templates,
  loading,
  refetch,
}: ReportTemplatesTableProps) {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const openDialog = useSetRecoilState(dialogState);
  const navigate = useNavigate();
  const setConfirmDialog = useSetRecoilState(confirmDialogState);
  const setToast = useSetRecoilState(toastStateSelector);

  const onEdit = (reportTemplateType: FormTemplateType) => {
    const path = getRoutePath(RoutePaths.reportTemplateEditor, {
      formTemplateTypeId: reportTemplateType.id,
      projectId,
    });

    navigate(path);
  };

  const onConfirmDelete = (templateId: string) => {
    return async () => {
      try {
        await remove({
          type: ModelTypes.formTemplateType,
          ids: {
            id: templateId,
            projectId: projectId,
          },
        });

        refetch?.();

        setConfirmDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('projectCreation.errors.deletionFailed'),
          severity: ToastSeverity.error,
        });
      }
    };
  };

  return (
    <>
      <Box display="flex" flexDirection="row" justifyContent={'space-between'}>
        <Typography variant="h5" ml={1} mt={2}>
          {t('reports.reportTemplates')}
        </Typography>
        <Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            sx={{ mr: 1, mt: 2 }}
            onClick={() => {
              openDialog({
                isOpen: true,
                dialogType: DialogTypes.reportTemplate,
              });
            }}
          >
            {t('reports.addReportTemplate')}
          </Button>
        </Box>
      </Box>
      <TableContainer sx={{ mt: 1, pb: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                {t('general.name')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                {t('general.version')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                {t('general.operations')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ position: 'relative' }}>
            {loading ? (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
              />
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography align="center">{t('general.noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map(reportTemplate => {
                const latestTemplate = getLatestTemplate(
                  reportTemplate.formTemplates,
                );
                return (
                  <TableRow key={`report-templates-table-${reportTemplate.id}`}>
                    <TableCell align="left">{reportTemplate.name}</TableCell>
                    <TableCell align="left">
                      <Chip
                        color={
                          latestTemplate?.status ===
                          FormTemplateStatus.Published
                            ? 'success'
                            : 'warning'
                        }
                        label={
                          latestTemplate?.version
                            ? `${latestTemplate?.version ?? ''}. ${t(
                                'general.version',
                              )}`
                            : t('forms.formTemplateStatus.draft')
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(reportTemplate)}
                      >
                        <Tooltip title={t('general.edit')}>
                          <Edit fontSize="small" />
                        </Tooltip>
                      </IconButton>

                      <IconButton size="small">
                        <Tooltip
                          title={t('general.delete')}
                          onClick={() => {
                            setConfirmDialog({
                              isOpen: true,
                              dialogType: DialogTypes.confirmDelete,
                              action: onConfirmDelete(reportTemplate.id),
                            });
                          }}
                        >
                          <Delete fontSize="small" />
                        </Tooltip>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
